import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import dayjs from 'dayjs';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { KpiEvaluationPreviewDialogComponent } from 'src/app/components/main/employees/performance/my-metrics/kpi-evaluation-preview-dialog/kpi-evaluation-preview-dialog.component';
import { ClockRegisterJournalClockTypeEnum } from 'src/app/enums/clock-register-journal-clock-type.enum';
import { ClockRegisterTypeEnum } from 'src/app/enums/clock-register-type.enum';
import { DayOfWeekWithLabels } from 'src/app/enums/day-of-week.enum';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { ClockRegister } from 'src/app/models/clock-register.model';
import { getKpiEvaluationStyle } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import {
  getNonWorkingDayAlertReasonEmoji,
  getNonWorkingDayAlertReasonTooltip,
} from 'src/app/enums/working-contract-weekly-schedule-exception-request-non-working-day-alert-reason.enum';
import {
  WorkingContractWeeklyScheduleExceptionRequest,
  WorkingContractWeeklyScheduleExceptionRequestDayPeriod,
  WorkingContractWeeklyScheduleExceptionRequestDayPeriodSide,
  WorkingContractWeeklyScheduleExceptionRequestWeekDayIndicator,
} from 'src/app/models/working-contract-weekly-schedule-exception-request.model';
import { HourDecimalToHoursMinutesPipe } from 'src/app/pipes/hour-decimal-to-hours-minutes.pipe';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-working-contract-weekly-schedule-exception-request-detail-dialog',
  templateUrl: './working-contract-weekly-schedule-exception-request-detail-dialog.component.html',
  styleUrl: './working-contract-weekly-schedule-exception-request-detail-dialog.component.css',
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatTooltipModule,
    MatIconButton,
    MatIcon,
    HourDecimalToHoursMinutesPipe,
  ],
})
export class WorkingContractWeeklyScheduleExceptionRequestDetailDialogComponent {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly dialog_ref = inject(MatDialogRef<WorkingContractWeeklyScheduleExceptionRequestDetailDialogComponent, boolean>);
  private readonly data = inject<WorkingContractWeeklyScheduleExceptionRequestDetailDialogData>(MAT_DIALOG_DATA);

  protected readonly saving = signal(false);

  private readonly detail = toSignal(
    forkJoin({
      request: this.api.getWorkingContractWeeklyScheduleExceptionRequest(this.data.id, {
        with: 'working_contract_weekly_schedule_exception_request_day_periods,user,working_contract_weekly_schedule_exception.working_contract.current_working_contract_variable_condition,employee_request_responsible_for_answers',
        append: 'week_day_indicators',
      }),
      clock_registers: this.loadClockRegisters(
        this.data.user_id,
        toMadridDate(this.data.start_week_date),
        clockRegistersRangeEnd(this.data.end_week_date, this.data.created_at)
      ),
    }).pipe(
      catchError((): Observable<null> => {
        this.dialog_ref.close();
        return of(null);
      })
    ),
    { initialValue: null }
  );

  protected readonly loading = computed((): boolean => this.detail() === null);
  protected readonly request = computed((): WorkingContractWeeklyScheduleExceptionRequest | null => this.detail()?.request ?? null);

  private readonly clock_registers = computed((): ClockRegister[] => this.detail()?.clock_registers ?? []);

  protected readonly user_name = computed((): string => this.request()?.user?.full_name ?? '');

  protected readonly week_label = computed((): string => {
    const request = this.request();

    if (request === null) {
      return '';
    }

    const week = getRequestWeekBounds(request);

    return formatMadridWeekRange(week.start, week.end);
  });

  protected readonly is_pending = computed((): boolean => this.request()?.status === EmployeeRequestResponsibleForAnswerStatusEnum.PENDING);

  protected readonly status_label = computed((): string => {
    const request = this.request();
    return request ? EmployeeRequestResponsibleForAnswerStatusLabels[request.status] : '';
  });

  protected readonly proposed_hours = computed((): number => {
    const periods = this.request()?.working_contract_weekly_schedule_exception_request_day_periods ?? [];
    const proposed_minutes = periods
      .filter((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): boolean => period.side === 'proposed')
      .reduce(
        (total: number, period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): number =>
          total + (timeToMinutes(period.end_time) - timeToMinutes(period.start_time)),
        0
      );

    return Math.round((proposed_minutes / 60) * 100) / 100;
  });

  protected readonly expected_hours = computed(
    (): number | null =>
      this.request()?.working_contract_weekly_schedule_exception?.working_contract?.current_working_contract_variable_condition
        ?.hours_per_week_expected ?? null
  );

  protected readonly proposed_hours_mismatch_expected = computed((): boolean => {
    const expected = this.expected_hours();
    if (expected === null) {
      return false;
    }

    return Math.round(this.proposed_hours() * 60) !== Math.round(expected * 60);
  });

  protected readonly can_open_current_kpi_detail = computed((): boolean => this.request() != null);

  protected readonly current_kpi_chip_label = computed((): string => {
    const request = this.request();

    if (request == null) {
      return '';
    }

    return evaluationChipLabel(request.current_schedule_compliance_kpi_evaluation, request.current_schedule_compliance_kpi_value);
  });

  protected readonly proposed_kpi_chip_label = computed((): string => {
    const request = this.request();

    if (request == null) {
      return '';
    }

    return evaluationChipLabel(request.proposed_schedule_compliance_kpi_evaluation, request.proposed_schedule_compliance_kpi_value);
  });

  private readonly axis = computed((): TimelineAxis => {
    const request = this.request();
    let min_minute = Number.POSITIVE_INFINITY;
    let max_minute = Number.NEGATIVE_INFINITY;

    const extend = (start: number, end: number): void => {
      min_minute = Math.min(min_minute, start);
      max_minute = Math.max(max_minute, end);
    };

    for (const period of request?.working_contract_weekly_schedule_exception_request_day_periods ?? []) {
      extend(timeToMinutes(period.start_time), timeToMinutes(period.end_time));
    }
    for (const range of this.clock_register_ranges().flatMap((day: ClockRegisterDayRanges) => day.ranges)) {
      extend(range.start, range.end);
    }

    const start_hour = Number.isFinite(min_minute) ? Math.floor(min_minute / 60) : DEFAULT_AXIS_START_HOUR;
    const end_hour = Number.isFinite(max_minute) ? Math.ceil(max_minute / 60) : DEFAULT_AXIS_END_HOUR;

    return {
      start_minute: start_hour * 60,
      end_minute: Math.max(end_hour, start_hour + 1) * 60,
    };
  });

  protected readonly axis_hours = computed((): AxisHourTick[] => {
    const { start_minute, end_minute } = this.axis();
    const hours: AxisHourTick[] = [];
    for (let minute = start_minute; minute <= end_minute; minute += 60) {
      hours.push({ hour: minute / 60, left_percent: ((minute - start_minute) / (end_minute - start_minute)) * 100 });
    }

    return hours;
  });

  protected readonly days = computed((): DayTimeline[] => {
    const request = this.request();
    if (request === null) {
      return [];
    }

    const periods: WorkingContractWeeklyScheduleExceptionRequestDayPeriod[] =
      request.working_contract_weekly_schedule_exception_request_day_periods ?? [];
    const week = getRequestWeekBounds(request);
    const week_monday = dayjs(toMadridDate(week.start));
    const request_at = request.created_at != null && request.created_at !== '' ? dayjs(request.created_at).tz(MADRID_TZ) : null;
    const request_date = request_at?.format('YYYY-MM-DD') ?? null;
    const clock_ranges_by_date = new Map(
      this.clock_register_ranges().map((day: ClockRegisterDayRanges): [string, ClockMinuteRange[]] => [day.date, day.ranges])
    );

    const indicators_by_day = new Map(
      (request.week_day_indicators ?? []).map(
        (
          indicator: WorkingContractWeeklyScheduleExceptionRequestWeekDayIndicator
        ): [string, WorkingContractWeeklyScheduleExceptionRequestWeekDayIndicator] => [indicator.day_of_week, indicator]
      )
    );

    const { start_minute: axis_start, end_minute: axis_end } = this.axis();

    return DayOfWeekWithLabels.map((day, index): DayTimeline => {
      const date = week_monday.add(index, 'day');
      const date_key = date.format('YYYY-MM-DD');
      const day_periods = periods.filter(
        (period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): boolean => period.day_of_week === day.key
      );
      const current_keys = new Set(
        day_periods
          .filter((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): boolean => period.side === 'current')
          .map((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): string =>
            this.periodKey(period.start_time, period.end_time)
          )
      );
      const proposed_keys = new Set(
        day_periods
          .filter((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): boolean => period.side === 'proposed')
          .map((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): string =>
            this.periodKey(period.start_time, period.end_time)
          )
      );
      const cutoff_minutes = this.pastCutoffMinutesForDay(date_key, request_at);

      const toBars = (side: WorkingContractWeeklyScheduleExceptionRequestDayPeriodSide): TimelineBar[] =>
        day_periods
          .filter((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): boolean => period.side === side)
          .sort(
            (
              a: WorkingContractWeeklyScheduleExceptionRequestDayPeriod,
              b: WorkingContractWeeklyScheduleExceptionRequestDayPeriod
            ): number => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
          )
          .map((period: WorkingContractWeeklyScheduleExceptionRequestDayPeriod): TimelineBar => {
            const start = timeToMinutes(period.start_time);
            const end = timeToMinutes(period.end_time);
            const key = this.periodKey(period.start_time, period.end_time);
            const already_past = cutoff_minutes !== null && end <= cutoff_minutes;
            let kind = TimelineBarKind.Unchanged;

            if (side === 'current') {
              if (!already_past && !proposed_keys.has(key)) {
                kind = TimelineBarKind.Removed;
              }
            } else if (!current_keys.has(key)) {
              kind = TimelineBarKind.Changed;
            }

            return this.buildBar(start, end, kind);
          });

      const current = toBars('current');
      const proposed = toBars('proposed');
      const clock_registers = (clock_ranges_by_date.get(date_key) ?? []).map(
        (range: ClockMinuteRange): TimelineBar => this.buildBar(range.start, range.end, TimelineBarKind.Clock)
      );

      let request_marker_left_percent: number | null = null;
      if (request_at != null && request_date === date_key) {
        const marker_minute = request_at.hour() * 60 + request_at.minute();
        request_marker_left_percent = ((marker_minute - axis_start) / (axis_end - axis_start)) * 100;
        if (request_marker_left_percent < 0 || request_marker_left_percent > 100) {
          request_marker_left_percent = null;
        }
      }

      const indicator = indicators_by_day.get(day.key)!;

      return {
        key: day.key,
        title: `${day.label.toUpperCase()} ${date.date()}`,
        location_emoji: indicator.is_remote ? '🏠' : '🏢',
        location_tooltip: indicator.is_remote ? 'Teletrabajo' : 'Presencial',
        off_emoji: getNonWorkingDayAlertReasonEmoji(indicator.reason),
        off_tooltip: getNonWorkingDayAlertReasonTooltip(indicator.reason),
        current,
        proposed,
        clock_registers,
        request_marker_left_percent,
        alert_messages: indicator.alert_messages,
      };
    });
  });

  private readonly clock_register_ranges = computed((): ClockRegisterDayRanges[] => {
    const request = this.request();
    if (request === null) {
      return [];
    }

    const week = getRequestWeekBounds(request);
    const week_start = toMadridDate(week.start);
    const week_end = toMadridDate(week.end);
    const request_at =
      request.created_at != null && request.created_at !== '' ? dayjs(request.created_at).tz(MADRID_TZ) : dayjs().tz(MADRID_TZ);

    const registers = this.clock_registers()
      .filter((register: ClockRegister): boolean => register.journal_clock_type === ClockRegisterJournalClockTypeEnum.SCHEDULE)
      .map(
        (register: ClockRegister): ClockRegisterMoment => ({
          type: register.type,
          at: dayjs(register.created_at).tz(MADRID_TZ),
        })
      )
      .filter((register: ClockRegisterMoment): boolean => {
        const date = register.at.format('YYYY-MM-DD');
        return date >= week_start && date <= week_end && !register.at.isAfter(request_at);
      })
      .sort((a: ClockRegisterMoment, b: ClockRegisterMoment): number => a.at.valueOf() - b.at.valueOf());

    const by_date = new Map<string, ClockMinuteRange[]>();
    let open: dayjs.Dayjs | null = null;
    const minutesOf = (value: dayjs.Dayjs): number => value.hour() * 60 + value.minute();
    const push = (date: string, start: number, end: number): void => {
      by_date.set(date, [...(by_date.get(date) ?? []), { start, end }]);
    };

    for (const register of registers) {
      if (register.type === ClockRegisterTypeEnum.IN) {
        open = register.at;
        continue;
      }

      if (open !== null) {
        const open_date = open.format('YYYY-MM-DD');
        const end = open_date === register.at.format('YYYY-MM-DD') ? minutesOf(register.at) : 24 * 60;
        push(open_date, minutesOf(open), end);
        open = null;
      }
    }

    if (open !== null && open.format('YYYY-MM-DD') === request_at.format('YYYY-MM-DD')) {
      push(open.format('YYYY-MM-DD'), minutesOf(open), minutesOf(request_at));
    }

    return [...by_date.entries()].map(
      ([date, ranges]: [string, ClockMinuteRange[]]): ClockRegisterDayRanges => ({
        date,
        ranges,
      })
    );
  });

  protected openCurrentScheduleComplianceKpiDetail(): void {
    const request = this.request();
    if (request == null) {
      return;
    }

    this.dialog.open(KpiEvaluationPreviewDialogComponent, {
      data: {
        kind: 'fetch',
        month: dayjs(getRequestWeekBounds(request).start).tz(MADRID_TZ).format('YYYY-MM'),
        production_key_performance_indicator_id: 1,
        user_id: request.user_id,
      },
      panelClass: 'kpi-preview-dialog-panel',
      autoFocus: false,
    });
  }

  protected accept(): void {
    this.answer(EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED, '¿Estás seguro de que quieres aceptar esta solicitud?');
  }

  protected reject(): void {
    this.answer(EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED, '¿Estás seguro de que quieres rechazar esta solicitud?');
  }

  private answer(
    status: EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED | EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED,
    confirm_message: string
  ): void {
    const request = this.request();
    if (request === null || this.saving() || !window.confirm(confirm_message)) {
      return;
    }

    this.saving.set(true);
    this.api.updateWorkingContractWeeklyScheduleExceptionRequest(request.id, { status }).subscribe({
      next: (): void => {
        this.saving.set(false);
        this.dialog_ref.close(true);
      },
      error: (): void => this.saving.set(false),
    });
  }

  private loadClockRegisters(user_id: number, range_start: string, range_end: string): Observable<ClockRegister[]> {
    const periods = clockRegisterPeriodsForDateRange(range_start, range_end);

    if (periods.length === 1) {
      return this.api
        .getClockRegistersByUserId(user_id.toString(), { period: periods[0] })
        .pipe(catchError((): Observable<ClockRegister[]> => of([] as ClockRegister[])));
    }

    return forkJoin(
      periods.map(
        (period: string): Observable<ClockRegister[]> =>
          this.api
            .getClockRegistersByUserId(user_id.toString(), { period })
            .pipe(catchError((): Observable<ClockRegister[]> => of([] as ClockRegister[])))
      )
    ).pipe(map((results: ClockRegister[][]): ClockRegister[] => results.flat()));
  }

  private buildBar(start_minute: number, end_minute: number, kind: TimelineBarKind): TimelineBar {
    const { start_minute: axis_start, end_minute: axis_end } = this.axis();
    const total = axis_end - axis_start;
    const clamped_start = Math.max(start_minute, axis_start);
    const clamped_end = Math.min(end_minute, axis_end);
    const duration_minutes = Math.max(end_minute - start_minute, 0);

    return {
      left_percent: ((clamped_start - axis_start) / total) * 100,
      width_percent: (Math.max(clamped_end - clamped_start, 0) / total) * 100,
      label: `${this.formatMinutes(start_minute)} - ${this.formatMinutes(end_minute)}`,
      kind,
      is_compact: duration_minutes < COMPACT_BAR_DURATION_MINUTES,
    };
  }

  private periodKey(start_time: string, end_time: string): string {
    return `${this.formatMinutes(timeToMinutes(start_time))}-${this.formatMinutes(timeToMinutes(end_time))}`;
  }

  private pastCutoffMinutesForDay(date_key: string, request_at: dayjs.Dayjs | null): number | null {
    if (request_at === null) {
      return null;
    }

    const request_date = request_at.format('YYYY-MM-DD');
    if (date_key < request_date) {
      return 24 * 60;
    }

    if (date_key > request_date) {
      return null;
    }

    return request_at.hour() * 60 + request_at.minute();
  }

  private formatMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60) % 24;
    return `${hours.toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;
  }
}

interface WorkingContractWeeklyScheduleExceptionRequestDetailDialogData {
  id: number;
  user_id: number;
  created_at: string | undefined;
  start_week_date: string | Date;
  end_week_date: string | Date;
}

enum TimelineBarKind {
  Changed = 'changed',
  Removed = 'removed',
  Unchanged = 'unchanged',
  Clock = 'clock',
}

interface TimelineBar {
  left_percent: number;
  width_percent: number;
  label: string;
  kind: TimelineBarKind;
  is_compact: boolean;
}

interface TimelineAxis {
  start_minute: number;
  end_minute: number;
}

interface AxisHourTick {
  hour: number;
  left_percent: number;
}

interface ClockMinuteRange {
  start: number;
  end: number;
}

interface ClockRegisterDayRanges {
  date: string;
  ranges: ClockMinuteRange[];
}

interface ClockRegisterMoment {
  type: ClockRegisterTypeEnum;
  at: dayjs.Dayjs;
}

interface DayTimeline {
  key: string;
  title: string;
  location_emoji: string;
  location_tooltip: string;
  off_emoji: string | null;
  off_tooltip: string | null;
  current: TimelineBar[];
  proposed: TimelineBar[];
  clock_registers: TimelineBar[];
  request_marker_left_percent: number | null;
  alert_messages: string[];
}

const MADRID_TZ = 'Europe/Madrid';
const DEFAULT_AXIS_START_HOUR = 7;
const DEFAULT_AXIS_END_HOUR = 19;
const COMPACT_BAR_DURATION_MINUTES = 100;

function toMadridDate(value: string | Date): string {
  return dayjs(value).tz(MADRID_TZ).format('YYYY-MM-DD');
}

function clockRegisterPeriodsForDateRange(range_start: string, range_end: string): string[] {
  const periods: string[] = [];
  let cursor = dayjs(range_start).startOf('month');
  const last = dayjs(range_end).startOf('month');

  while (cursor.isBefore(last) || cursor.isSame(last, 'month')) {
    periods.push(cursor.format('YYYY/M'));
    cursor = cursor.add(1, 'month');
  }

  return periods.length > 0 ? periods : [dayjs(range_start).format('YYYY/M')];
}

function clockRegistersRangeEnd(end_week_date: string | Date, created_at: string | undefined): string {
  const week_end = toMadridDate(end_week_date);
  const request_date = created_at != null && created_at !== '' ? dayjs(created_at).tz(MADRID_TZ).format('YYYY-MM-DD') : week_end;

  return week_end < request_date ? week_end : request_date;
}

function getRequestWeekBounds(request: WorkingContractWeeklyScheduleExceptionRequest): { start: string | Date; end: string | Date } {
  const exception = request.working_contract_weekly_schedule_exception!;

  return { start: exception.start_week_date, end: exception.end_week_date };
}

export function formatMadridWeekRange(start_week_date: string | Date, end_week_date: string | Date): string {
  return `${dayjs(start_week_date).tz(MADRID_TZ).format('DD/MM/YYYY')} - ${dayjs(end_week_date).tz(MADRID_TZ).format('DD/MM/YYYY')}`;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function evaluationChipLabel(evaluation: ProductionKeyPerformanceIndicatorEvaluationEnum, value: number | null | undefined): string {
  const label = getKpiEvaluationStyle(evaluation).label;
  if (value == null) {
    return label;
  }
  return `${label} (${Math.round(value)}%)`;
}
